/**
 * Lightweight OS tooltip portal — glass dark chips for toolbar icons etc.
 * ctx.ui.tooltip(el, text) or data-os-tooltip attribute (auto-bound).
 */
const STYLE_ID = "arclog-tooltip-css";

function ensureCss() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.os-tip {
  position: fixed;
  z-index: 400;
  max-width: 260px;
  padding: 6px 10px;
  border-radius: 8px;
  border: 1px solid rgba(255,255,255,.14);
  background: rgba(22,24,32,.94);
  color: var(--os-text, #f2f4f8);
  font: 11.5px/1.35 var(--os-font-ui, system-ui);
  box-shadow: 0 10px 28px rgba(0,0,0,.45);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  pointer-events: none;
  opacity: 0;
  transform: translateY(2px);
  transition: opacity .12s ease, transform .12s ease;
}
.os-tip.is-on {
  opacity: 1;
  transform: translateY(0);
}
`;
  document.head.appendChild(s);
}

let tipEl: HTMLDivElement | null = null;
let hideTimer: number | null = null;
const bound = new WeakSet<HTMLElement>();

function tipNode() {
  ensureCss();
  if (!tipEl) {
    tipEl = document.createElement("div");
    tipEl.className = "os-tip";
    tipEl.setAttribute("role", "tooltip");
    document.body.appendChild(tipEl);
  }
  return tipEl;
}

function place(el: HTMLElement, text: string) {
  const t = tipNode();
  t.textContent = text;
  t.classList.add("is-on");
  const r = el.getBoundingClientRect();
  const tw = Math.min(260, Math.max(40, t.offsetWidth || 120));
  const th = t.offsetHeight || 28;
  let left = r.left + r.width / 2 - tw / 2;
  let top = r.bottom + 8;
  if (top + th > window.innerHeight - 8) top = r.top - th - 8;
  left = Math.max(8, Math.min(left, window.innerWidth - tw - 8));
  t.style.left = left + "px";
  t.style.top = top + "px";
}

function hide() {
  if (hideTimer) window.clearTimeout(hideTimer);
  hideTimer = window.setTimeout(() => {
    tipEl?.classList.remove("is-on");
  }, 40);
}

export function attachTooltip(el: HTMLElement, text: string): () => void {
  if (!text) return () => {};
  const show = () => {
    if (hideTimer) window.clearTimeout(hideTimer);
    place(el, text);
  };
  const onLeave = () => hide();
  el.addEventListener("mouseenter", show);
  el.addEventListener("mouseleave", onLeave);
  el.addEventListener("focus", show);
  el.addEventListener("blur", onLeave);
  bound.add(el);
  return () => {
    el.removeEventListener("mouseenter", show);
    el.removeEventListener("mouseleave", onLeave);
    el.removeEventListener("focus", show);
    el.removeEventListener("blur", onLeave);
    hide();
  };
}

/** Scan for [data-os-tooltip] under root (one-time + mutation-light). */
export function bindTooltipAttribute(root: ParentNode = document) {
  ensureCss();
  const walk = (node: ParentNode) => {
    const list =
      node instanceof Element && node.matches?.("[data-os-tooltip]")
        ? [node, ...Array.from(node.querySelectorAll("[data-os-tooltip]"))]
        : Array.from(
            (node as Document | Element).querySelectorAll?.("[data-os-tooltip]") ||
              []
          );
    for (const el of list) {
      const h = el as HTMLElement;
      if (bound.has(h)) continue;
      const text = h.getAttribute("data-os-tooltip") || "";
      if (text) attachTooltip(h, text);
    }
  };
  walk(root);
}

export function createTooltipApi() {
  ensureCss();
  return {
    attach: attachTooltip,
    /** Convenience: set data attribute + bind */
    set(el: HTMLElement, text: string) {
      el.setAttribute("data-os-tooltip", text);
      return attachTooltip(el, text);
    },
  };
}
